import {
  getPendingBalance,
  hasScheduledItems,
  getPlanPatientName
} from "./planQueuePresentation"

export function buildPatientFocusedMetrics(filteredPlans) {
  const allItems = filteredPlans.flatMap((plan) =>
    Array.isArray(plan.items) ? plan.items : []
  )

  const pendingItems = allItems.filter((item) => {
    const status = String(item.status || "").toUpperCase()
    return !["DONE", "CANCELLED"].includes(status)
  }).length

  const scheduledItems = allItems.filter(
    (item) => String(item.status || "").toUpperCase() === "SCHEDULED"
  ).length

  const doneItems = allItems.filter(
    (item) => String(item.status || "").toUpperCase() === "DONE"
  ).length

  const totalBalance = filteredPlans.reduce((acc, plan) => {
    return acc + getPendingBalance(plan)
  }, 0)

  const activePlans = filteredPlans.filter(
    (plan) => String(plan.status || "").toUpperCase() === "ACTIVE"
  ).length

  return {
    totalPlans: filteredPlans.length,
    activePlans,
    pendingItems,
    scheduledItems,
    doneItems,
    totalBalance
  }
}

export function buildPlansSummary(filteredPlans) {
  const totalEstimated = filteredPlans.reduce(
    (acc, item) => acc + Number(item.totalEstimated || 0),
    0
  )

  const totalProduced = filteredPlans.reduce(
    (acc, item) => acc + Number(item.totalProduced || 0),
    0
  )

  const totalPaid = filteredPlans.reduce(
    (acc, item) => acc + Number(item.totalPaid || 0),
    0
  )

  const active = filteredPlans.filter(
    (item) => String(item.status || "").toUpperCase() === "ACTIVE"
  ).length

  const completed = filteredPlans.filter(
    (item) => String(item.status || "").toUpperCase() === "COMPLETED"
  ).length

  const withBalance = filteredPlans.filter((item) => getPendingBalance(item) > 0).length

  return {
    totalPlans: filteredPlans.length,
    totalEstimated,
    totalProduced,
    totalPaid,
    active,
    completed,
    withBalance
  }
}

export function buildExecutiveInsights(filteredPlans) {
  const activePlans = filteredPlans.filter(
    (plan) => String(plan.status || "").toUpperCase() === "ACTIVE"
  )

  const productionPotentialPending = activePlans.reduce((acc, plan) => {
    const value = Number(plan.totalEstimated || 0) - Number(plan.totalProduced || 0)
    return acc + Math.max(0, value)
  }, 0)

  const collectionPotentialPending = activePlans.reduce((acc, plan) => {
    return acc + getPendingBalance(plan)
  }, 0)

  const plansWithoutAppointment = activePlans.filter((plan) => {
    return Array.isArray(plan.items) && plan.items.length > 0 && !hasScheduledItems(plan)
  }).length

  const topPatients = Object.values(
    activePlans.reduce((acc, plan) => {
      const patientKey = plan.patientId || `unknown-${plan.id}`
      const patientName = getPlanPatientName(plan)
      const pendingAmount = getPendingBalance(plan)

      if (!acc[patientKey]) {
        acc[patientKey] = {
          patientId: plan.patientId || "",
          patientName,
          pendingAmount: 0,
          plansCount: 0
        }
      }

      acc[patientKey].pendingAmount += pendingAmount
      acc[patientKey].plansCount += 1

      return acc
    }, {})
  )
    .sort((a, b) => b.pendingAmount - a.pendingAmount)
    .slice(0, 5)

  return {
    productionPotentialPending,
    collectionPotentialPending,
    plansWithoutAppointment,
    topPatients
  }
}